import { useState, useEffect } from "react";
import { toast } from "sonner";
import Modal from "@/components/Modal";
import ChannelPicker from "@/components/ChannelPicker";
import api, { formatApiError } from "@/lib/api";

const empty = () => ({ name: "", address: "", phone: "", email: "", notes: "", channels: ["sms"] });

function FieldRow({ label, testid, ...rest }) {
  return (
    <div>
      <span className="label-mono text-[var(--text-2)]">{label}</span>
      <input
        data-testid={testid}
        {...rest}
        className="mt-2 w-full h-11 px-3 border border-[var(--line)] bg-white outline-none focus:border-[var(--accent)]"
      />
    </div>
  );
}

export default function ClientDialog({ open, onClose, onSaved, client }) {
  const [form, setForm] = useState(empty());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setForm(client ? { ...empty(), ...client } : empty());
  }, [open, client]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Client name is required");
    setBusy(true);
    try {
      const payload = { name: form.name, address: form.address, phone: form.phone, email: form.email, notes: form.notes, channels: form.channels };
      if (client) await api.put(`/clients/${client.id}`, payload);
      else await api.post("/clients", payload);
      toast.success(client ? "Client updated" : "Client added");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} testid="client-dialog" subtitle="Business CRM" title={client ? "Edit client" : "Add client"}>
      <div className="space-y-4">
        <FieldRow label="Client name" testid="client-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="John Doe" />
        <FieldRow label="Address" testid="client-address" value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="123 Main St, Miami FL" />
        <div className="grid grid-cols-2 gap-3">
          <FieldRow label="Phone" testid="client-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+1 305 555 1234" />
          <FieldRow label="Email" testid="client-email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="john@example.com" />
        </div>
        <div>
          <span className="label-mono text-[var(--text-2)]">Notes</span>
          <textarea
            data-testid="client-notes"
            value={form.notes}
            onChange={(e) => set("notes", e.target.value)}
            rows={3}
            className="mt-2 w-full px-3 py-2 border border-[var(--line)] bg-white outline-none focus:border-[var(--accent)]"
          />
        </div>
        <ChannelPicker value={form.channels} onChange={(c) => set("channels", c)} />
        <div className="flex gap-3 pt-2">
          <button data-testid="client-cancel" onClick={onClose} className="flex-1 h-11 border border-[var(--line)] hover:bg-white transition-colors">
            Cancel
          </button>
          <button data-testid="client-save" onClick={save} disabled={busy} className="flex-1 h-11 bg-[var(--accent)] text-white hover:opacity-90 transition-opacity disabled:opacity-50">
            {busy ? "Saving…" : "Save client"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
